import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SeniorCenterApplicationService } from './senior-center-application.service';

@Injectable({
  providedIn: 'root'
})
export class FormApiService {
  private readonly http = inject(HttpClient);
  private readonly seniorCenterApplicationService = inject(SeniorCenterApplicationService);
  private readonly baseUrl = (environment as any).apiBaseUrl || environment.host || '';
  private staffEnrollmentUploadSession$: Observable<string> | null = null;

  constructor() {}



  login(payload: { email: string }): Observable<any> {
    const url = `${this.baseUrl}/api/auth/login`;

    return this.http.post(url, payload);
  }

  register(payload: any): Observable<any> {
    const url = `${this.baseUrl}/api/auth/register`;

    return this.http.post(url, payload);
  }

  verifyOtp(payload: { otp: string }, token?: string): Observable<any> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    const url = `${this.baseUrl}/api/auth/verify-otp`;

    return this.http.post(url, payload, { headers });
  }

  getProfile(): Observable<any> {
    const url = `${this.baseUrl}/api/auth/profile`;

    return this.http.get(url);
  }

  // Programs
  listPrograms(): Observable<any> {
    const url = `${this.baseUrl}/api/program/list`;

    return this.http.get(url);
  }

  getProgramDetail(id: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/${id}`;
    return this.http.get(url);
  }
  
  getProgram(id: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/form/${id}`;
    return this.http.get(url);
  }

  applyProgram(payload: any): Observable<any> {
    const url = `${this.baseUrl}/api/program/apply`;
    return this.http.post(url, payload);
  }

  submitStaticProgram(payload: any, endpoint?: string): Observable<any> {
    const url = endpoint 
      ? `${this.baseUrl}${endpoint}` 
      : `${this.baseUrl}/api/program/static-submit`;
    return this.http.post(url, payload);
  }

  getProgramSalesforceDetail(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/program-salesforce-detail?id=${programId}`;
    return this.http.get(url);
  }

  reuploadStaticProgram(payload: any): Observable<any> {
    const url = `${this.baseUrl}/api/program/staticform/reupload`;
    return this.http.put(url, payload);
  }

  getProgramSubmissions(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/submissions?program_id=${programId}`;
    return this.http.get(url);
  }

  // File endpoints
  getFile(path: string): Observable<Blob> {
    const url = `${this.baseUrl}/api/file/${path}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  // Get file with full response including headers (for extracting filename)
  getFileWithHeaders(path: string): Observable<{ blob: Blob; filename: string }> {
    const url = `${this.baseUrl}/api/file/${path}`;
    return this.http.get(url, { 
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        // Extract filename from response headers
        const contentDisposition = response.headers.get('content-disposition') || '';
        const filenameHeader = response.headers.get('filename') || '';
        
        // Try to get filename from 'filename' header first, then from content-disposition
        let filename = filenameHeader;
        
        if (!filename && contentDisposition) {
          // Extract filename from content-disposition header (e.g., "attachment; filename=example.pdf")
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
            // Decode URI if needed
            try {
              filename = decodeURIComponent(filename);
            } catch (e) {
              // If decoding fails, use as is
            }
          }
        }
        
        // Fallback to path if no filename found
        if (!filename) {
          filename = path.includes('.') ? path : `${path}.jpg`;
        }
        
        return {
          blob: response.body!,
          filename: filename
        };
      })
    );
  }

  uploadFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.baseUrl}/api/file/upload`;
    const uploadRequest = () => this.http.post(url, formData);

    // Existing file API expects participant auth. Staff Manage Services uploads
    // reuse the enrollment session token (same pattern as registration uploads).
    const staffLoggedIn =
      typeof sessionStorage !== 'undefined' &&
      !!sessionStorage.getItem('token') &&
      sessionStorage.getItem('token') !== 'undefined';

    if (this.isStaffEnrollmentMode() || (staffLoggedIn && !this.hasParticipantToken())) {
      return this.ensureStaffEnrollmentParticipantToken().pipe(switchMap(() => uploadRequest()));
    }

    return uploadRequest();
  }

  private hasParticipantToken(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    const token = sessionStorage.getItem('participant_auth_token');
    return !!token && token !== 'undefined' && !!token.trim();
  }

  private isStaffEnrollmentMode(): boolean {
    return (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem('staff_enrollment_mode') === 'true'
    );
  }

  /**
   * File upload uses existing /api/file/upload (participant auth).
   * Lazily start the existing staff enrollment session only when a token is needed.
   * On Continue / resume, reuse the application owner — do not create a placeholder user
   * (that caused family-member email conflicts on submit).
   */
  private ensureStaffEnrollmentParticipantToken(): Observable<string> {
    const existing = sessionStorage.getItem('participant_auth_token');
    if (existing && existing !== 'undefined' && existing.trim()) {
      return of(existing.trim());
    }

    if (!this.staffEnrollmentUploadSession$) {
      const ownedUserId = Number(sessionStorage.getItem('staff_enrollment_user_id'));
      if (Number.isFinite(ownedUserId) && ownedUserId > 0) {
        const userHost =
          (environment as { userHost?: string }).userHost ?? environment.host;
        this.staffEnrollmentUploadSession$ = this.http
          .post<{ token?: string; Token?: string }>(
            `${userHost}/api/auth/internal/issue-auth-token`,
            { userId: ownedUserId }
          )
          .pipe(
            map((response) => {
              const token = (response?.token ?? response?.Token)?.trim();
              if (!token) {
                throw new Error('Unable to issue participant token for file upload');
              }
              sessionStorage.setItem('participant_auth_token', token);
              return token;
            }),
            shareReplay(1),
            catchError((error) => {
              this.staffEnrollmentUploadSession$ = null;
              return throwError(() => error);
            })
          );
      } else {
        this.staffEnrollmentUploadSession$ = this.seniorCenterApplicationService
          .startEnrollmentSession()
          .pipe(
            map((response) => {
              const token = response?.token?.trim();
              if (!token) {
                throw new Error('Unable to start enrollment session for file upload');
              }
              if (response?.userId) {
                sessionStorage.setItem(
                  'staff_enrollment_session_user_id',
                  String(response.userId)
                );
              }
              sessionStorage.setItem('participant_auth_token', token);
              return token;
            }),
            shareReplay(1),
            catchError((error) => {
              this.staffEnrollmentUploadSession$ = null;
              return throwError(() => error);
            })
          );
      }
    }

    return this.staffEnrollmentUploadSession$;
  }

  // Service endpoints
  getServiceList(): Observable<any> {
    const url = `${this.baseUrl}/api/program/service/list`;
    return this.http.get(url);
  }

  getServiceVendors(serviceId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/service/vendors?service_id=${serviceId}`;
    return this.http.get(url);
  }

  // Get program user data
  getProgramUser(programId: string | number, applicationId?: string | number): Observable<any> {
    const appParam = applicationId != null && String(applicationId).trim() !== ''
      ? `&application_id=${encodeURIComponent(String(applicationId))}`
      : '';
    const url = `${this.baseUrl}/api/program/user?program_id=${programId}${appParam}`;
    return this.http.get(url);
  }

  // Get program data by user ID (new API)
  getProgramDataByUserId(userId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/user/programdata?user_id=${userId}`;
    return this.http.get(url);
  }

  // Get family members by user ID (new API)
  getFamilyMembersByUserId(userId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/familymembers?user_id=${userId}`;
    return this.http.get(url);
  }

  /**
   * Current user's application for a program (user_program row).
   * GET /api/program/detail?program_id= — 404 if no application.
   */
  getUserProgramApplicationDetail(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/detail?program_id=${programId}`;
    return this.http.get(url);
  }

  /** Alias for getUserProgramApplicationDetail (historical name). */
  getProgramDraft(programId: string | number): Observable<any> {
    return this.getUserProgramApplicationDetail(programId);
  }

  /** Cancel/delete in-progress draft for a program (idempotent). */
  deleteProgramDraft(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/draft?program_id=${programId}`;
    return this.http.delete(url);
  }

  getJobStatus(jobId: string): Observable<any> {
    const url = `${this.baseUrl}/api/job/status/${jobId}`;
    return this.http.get(url);
  }

  checkIdentificationNumber(identificationNumber: string): Observable<any> {
    const url = `${this.baseUrl}/api/auth/check-identification-number`;
    return this.http.post(url, { identificationNumber });
  }

  // Get program count and application status
  getProgramCount(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/count?program_id=${programId}`;
    return this.http.get(url);
  }

}
